import { useEffect, useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api-client';
import {
  Loader2,
  MessageCircle,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Copy,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';

interface ConnectTelegramStepProps {
  formId: string;
  onComplete: () => void;
  onSkip: () => void;
  onBack: () => void;
}

interface Token {
  id: string;
  token: string;
  status: string;
  expires_at: string;
  methods?: {
    deep_link?: string;
  };
}

export function ConnectTelegramStep({ formId, onComplete, onSkip, onBack }: ConnectTelegramStepProps) {
  const [token, setToken] = useState<Token | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isCreatingToken, setIsCreatingToken] = useState(false);
  const { toast } = useToast();
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const botUsername = import.meta.env.VITE_BOT_USERNAME || 'formtocc_bot';

  // Create token on mount
  useEffect(() => {
    createToken();
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [formId]);

  // Poll for connection
  useEffect(() => {
    if (isConnected || !token) return;

    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = (await api.getFormChats(formId)) as { chats: unknown[] };
        if (response.chats && response.chats.length > 0) {
          setIsConnected(true);
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          toast({
            title: 'Telegram Connected!',
            description: 'Your chat is now linked to this form',
          });
          setTimeout(() => onComplete(), 1500);
        }
      } catch (error) {
        console.error('Error polling for chats:', error);
      }
    }, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [formId, token, isConnected, onComplete, toast]);

  const createToken = async () => {
    setIsCreatingToken(true);
    try {
      const result = (await api.createToken(formId)) as Token;
      setToken(result);
    } catch (error) {
      console.error('Failed to create token:', error);
      toast({
        title: 'Error',
        description: 'Failed to create binding token. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsCreatingToken(false);
    }
  };

  const handleCopyToken = () => {
    if (token) {
      navigator.clipboard.writeText(token.token);
      toast({
        title: 'Copied',
        description: 'Token copied to clipboard',
      });
    }
  };

  const handleRefreshToken = async () => {
    await createToken();
    toast({
      title: 'New token created',
      description: 'Scan the new QR code or use the new token',
    });
  };

  const deepLink = token?.methods?.deep_link || `https://t.me/${botUsername}?start=${token?.token}`;

  if (isConnected) {
    return (
      <Card className="mt-8">
        <CardContent className="pt-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-green-600 mb-2">Telegram Connected!</h3>
          <p className="text-gray-600">Redirecting to next step...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-8">
      <CardHeader className="text-center">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <MessageCircle className="w-6 h-6 text-blue-600" />
        </div>
        <CardTitle className="text-2xl">Connect Telegram</CardTitle>
        <CardDescription>
          Link your Telegram chat to receive form submissions instantly. You can connect a personal
          chat, group, or channel.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : token ? (
          <div className="space-y-6">
            {/* QR Code */}
            <div className="flex flex-col items-center">
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <QRCodeSVG value={deepLink} size={180} level="M" includeMargin={false} />
              </div>
              <p className="text-sm text-gray-500 mt-3">
                Scan with your phone's camera or Telegram app
              </p>
            </div>

            {/* Or divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or</span>
              </div>
            </div>

            {/* Deep link button */}
            <div className="flex flex-col items-center gap-3">
              <Button asChild className="w-full sm:w-auto">
                <a href={deepLink} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open in Telegram
                </a>
              </Button>

              {/* Manual token */}
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-1">
                  Or send this token to @{botUsername}:
                </p>
                <div className="flex items-center gap-2 justify-center">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                    {token.token}
                  </code>
                  <Button variant="ghost" size="sm" onClick={handleCopyToken}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Waiting indicator */}
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-blue-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">Waiting for connection...</span>
              </div>
              <p className="text-xs text-blue-500 mt-1">
                This page will update automatically when connected
              </p>
            </div>

            {/* Refresh token */}
            <div className="text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefreshToken}
                disabled={isCreatingToken}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${isCreatingToken ? 'animate-spin' : ''}`} />
                Generate new token
              </Button>
            </div>

            {/* Navigation */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button type="button" className="flex-1" onClick={onSkip}>
                Skip for now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">Failed to create binding token</p>
            <Button onClick={createToken} disabled={isCreatingToken}>
              {isCreatingToken ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Try Again'
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
